import React, { useRef, useState, useLayoutEffect, useMemo } from 'react'
import styled from 'styled-components/macro'
import { Tooltip } from 'src/components/Reusable/Tooltip/Tooltip'
import { Pill } from 'src/components/Reusable/Pill'
import { PlaceholderText } from 'src/components/Reusable/Inputs/Inputs.styles'
import {
  doAllItemsFit,
  calculateVisibleItems,
} from 'src/Utils/dropdownItemCalculation'

const OverflowContainer = styled.div`
  display: flex;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  align-items: center;
`

/**
 * Generic overflow pill component that shows "+ n more" with a tooltip
 * listing the remaining items.
 */
export interface OverflowPillProps<T> {
  remainingItems: T[]
  getDisplayName: (item: T) => string
  tooltipId: string
}

export const OverflowPill = <T,>({
  remainingItems,
  getDisplayName,
  tooltipId,
}: OverflowPillProps<T>): JSX.Element => {
  const tooltipContent = remainingItems.map(getDisplayName).join(', ')

  return (
    <Tooltip id={tooltipId} placement="right" tooltipBody={tooltipContent}>
      <span onClick={(e) => e.stopPropagation()}>
        <Pill label={`+ ${remainingItems.length} more`} useBorder />
      </span>
    </Tooltip>
  )
}

/**
 * Generic overflow dropdown toggle that handles:
 * - Measuring available container width
 * - Calculating which items fit vs overflow
 * - Rendering visible items with commas
 * - Showing "+ n more" pill for overflow items
 */
export interface OverflowDropdownToggleProps<T> {
  items: T[]
  getDisplayName: (item: T) => string
  getKey: (item: T) => string
  placeholder: string
  tooltipId: string
  renderItem?: (item: T) => JSX.Element
  minWidth?: number
}

export const OverflowDropdownToggle = <T,>({
  items,
  getDisplayName,
  getKey,
  placeholder,
  tooltipId,
  renderItem,
  minWidth = 150,
}: OverflowDropdownToggleProps<T>): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [availableWidth, setAvailableWidth] = useState<number | null>(null)

  useLayoutEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        // Account for padding and icon (approximately 60px)
        const width = containerRef.current.offsetWidth - 60
        setAvailableWidth(Math.max(width, minWidth))
      }
    }

    // Measure immediately
    updateWidth()

    const resizeObserver = new ResizeObserver(updateWidth)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [minWidth])

  const { displayedItems, remainingItems } = useMemo(() => {
    if (items.length === 0) {
      return { displayedItems: [], remainingItems: [] }
    }

    // Before width is measured, show all items (no overflow calculation)
    if (availableWidth === null) {
      return { displayedItems: items, remainingItems: [] }
    }

    // Create items with their display names for sorting/calculation
    const itemsWithNames = items.map((item) => ({
      item,
      name: getDisplayName(item),
    }))

    // First try alphabetical sorting to see if all items fit
    const alphabeticallySorted = [...itemsWithNames].sort((a, b) =>
      a.name.localeCompare(b.name),
    )

    if (doAllItemsFit(alphabeticallySorted, availableWidth)) {
      return {
        displayedItems: alphabeticallySorted.map((x) => x.item),
        remainingItems: [],
      }
    }

    // Otherwise, sort by length (shortest first) to maximize visible items
    const lengthSorted = [...itemsWithNames].sort(
      (a, b) => a.name.length - b.name.length,
    )
    const result = calculateVisibleItems(lengthSorted, availableWidth)

    // Fallback: if calculation resulted in no visible items but we have items,
    // show at least the first (shortest) one
    if (result.visible.length === 0 && lengthSorted.length > 0) {
      return {
        displayedItems: [lengthSorted[0].item],
        remainingItems: lengthSorted.slice(1).map((x) => x.item),
      }
    }

    return {
      displayedItems: result.visible.map((x) => x.item),
      remainingItems: result.remaining.map((x) => x.item),
    }
  }, [items, getDisplayName, availableWidth])

  const hasMore = remainingItems.length > 0

  return (
    <OverflowContainer ref={containerRef}>
      {items.length === 0 && <PlaceholderText>{placeholder}</PlaceholderText>}
      {displayedItems.map((item, index) => {
        const isLast = index === displayedItems.length - 1
        const separator = isLast ? '' : ',\u00A0'
        return (
          <React.Fragment key={getKey(item)}>
            {renderItem ? (
              <>
                {renderItem(item)}
                {separator}
              </>
            ) : (
              <span>
                {getDisplayName(item)}
                {separator}
              </span>
            )}
          </React.Fragment>
        )
      })}
      {hasMore && (
        <span style={{ marginLeft: displayedItems.length > 0 ? 8 : 0 }}>
          <OverflowPill
            remainingItems={remainingItems}
            getDisplayName={getDisplayName}
            tooltipId={tooltipId}
          />
        </span>
      )}
    </OverflowContainer>
  )
}
